// ─── EVIDENCE MIGRATION SWEEP ────────────────────────────────────────────────
// One-shot reset of legacy free-text evidence fields across every collection
// of an org, gated by `org.evidenceMigrationDone` on profile.json. Idempotent.
//
// The new Evidence collection (evidence/<id>.json) supersedes the previous
// free-text inputs that lived directly on each parent record. This sweep
// resets those legacy values to null/[]/"" before any view of the org first
// renders, so EvidenceSelector never has to coexist with stale free-text data.

import {
  readOrgFile,
  writeOrgFile,
  listOrgCollection,
} from './FileSystemAdapter';

const todayIso = () => new Date().toISOString().slice(0, 10);

// Push a changeLog entry on `record` describing the cleared field.
function pushClearLog(record, field, previousValue, newValue) {
  const log = Array.isArray(record.changeLog) ? [...record.changeLog] : [];
  log.push({
    timestamp: new Date().toISOString(),
    field,
    previousValue,
    newValue,
    changedBy: 'system',
  });
  return log;
}

// True if a value counts as "already cleared" (null/undefined/[]/"").
function isAlreadyEmpty(v, kind) {
  if (kind === 'array') return Array.isArray(v) && v.length === 0;
  if (kind === 'string') return v === '' || v == null;
  return v == null;
}

// Update a single record in `documents/`, `audits/`, `incidents/`, `suppliers/`,
// `metrics/`, `nonconformities/` style collections. `mutate` returns `true` if
// it actually changed something (so the file is rewritten), `false` otherwise.
async function processItemCollection(orgId, folder, mutate) {
  let items = [];
  try {
    items = await listOrgCollection(orgId, folder);
  } catch {
    return;
  }
  for (const item of items) {
    if (!item || !item.id) continue;
    const dirty = mutate(item);
    if (dirty) {
      item.updatedAt = new Date().toISOString();
      await writeOrgFile(orgId, `${folder}/${item.id}.json`, item);
    }
  }
}

// ── PUBLIC API ────────────────────────────────────────────────────────────────
export async function runEvidenceMigrationIfNeeded(orgId) {
  let profile;
  try {
    profile = await readOrgFile(orgId, 'profile.json');
  } catch {
    return;
  }
  if (!profile || profile.evidenceMigrationDone === true) return;

  // ── documents/<id>.json ────────────────────────────────────────────────────
  await processItemCollection(orgId, 'documents', (doc) => {
    let dirty = false;
    if (!isAlreadyEmpty(doc.approvalEvidenceId, 'value')) {
      doc.changeLog = pushClearLog(doc, 'approvalEvidenceId', doc.approvalEvidenceId, null);
      doc.approvalEvidenceId = null;
      dirty = true;
    }
    return dirty;
  });

  // ── scope.json ─────────────────────────────────────────────────────────────
  try {
    const scope = await readOrgFile(orgId, 'scope.json');
    if (scope && !isAlreadyEmpty(scope.approvalEvidenceId, 'value')) {
      scope.changeLog = pushClearLog(scope, 'approvalEvidenceId', scope.approvalEvidenceId, null);
      scope.approvalEvidenceId = null;
      scope.updatedAt = new Date().toISOString();
      await writeOrgFile(orgId, 'scope.json', scope);
    }
  } catch { /* file absent */ }

  // ── soa.json ───────────────────────────────────────────────────────────────
  try {
    const soa = await readOrgFile(orgId, 'soa.json');
    if (soa) {
      let dirty = false;
      if (!isAlreadyEmpty(soa.approvalEvidenceId, 'value')) {
        soa.changeLog = pushClearLog(soa, 'approvalEvidenceId', soa.approvalEvidenceId, null);
        soa.approvalEvidenceId = null;
        dirty = true;
      }
      if (Array.isArray(soa.controls)) {
        soa.controls.forEach((ctrl, idx) => {
          if (ctrl && !isAlreadyEmpty(ctrl.evidenceIds, 'array')) {
            soa.changeLog = pushClearLog(soa, `controls[${idx}].evidenceIds`, ctrl.evidenceIds, []);
            ctrl.evidenceIds = [];
            dirty = true;
          }
        });
      }
      if (dirty) {
        soa.updatedAt = new Date().toISOString();
        await writeOrgFile(orgId, 'soa.json', soa);
      }
    }
  } catch { /* file absent */ }

  // ── rtp.json ───────────────────────────────────────────────────────────────
  try {
    const rtp = await readOrgFile(orgId, 'rtp.json');
    if (rtp && !isAlreadyEmpty(rtp.approvalEvidenceId, 'value')) {
      rtp.changeLog = pushClearLog(rtp, 'approvalEvidenceId', rtp.approvalEvidenceId, null);
      rtp.approvalEvidenceId = null;
      rtp.updatedAt = new Date().toISOString();
      await writeOrgFile(orgId, 'rtp.json', rtp);
    }
  } catch { /* file absent */ }

  // ── audits/<id>.json ───────────────────────────────────────────────────────
  await processItemCollection(orgId, 'audits', (audit) => {
    let dirty = false;
    if (!isAlreadyEmpty(audit.evidenceIds, 'array')) {
      audit.changeLog = pushClearLog(audit, 'evidenceIds', audit.evidenceIds, []);
      audit.evidenceIds = [];
      dirty = true;
    }
    if (Array.isArray(audit.findings)) {
      audit.findings.forEach((f, idx) => {
        if (f && !isAlreadyEmpty(f.evidenceIds, 'array')) {
          audit.changeLog = pushClearLog(audit, `findings[${idx}].evidenceIds`, f.evidenceIds, []);
          f.evidenceIds = [];
          dirty = true;
        }
      });
    }
    return dirty;
  });

  // ── incidents/<id>.json ────────────────────────────────────────────────────
  await processItemCollection(orgId, 'incidents', (inc) => {
    if (isAlreadyEmpty(inc.evidenceIds, 'array')) return false;
    inc.changeLog = pushClearLog(inc, 'evidenceIds', inc.evidenceIds, []);
    inc.evidenceIds = [];
    return true;
  });

  // ── suppliers/<id>.json ────────────────────────────────────────────────────
  await processItemCollection(orgId, 'suppliers', (sup) => {
    if (isAlreadyEmpty(sup.evidenceIds, 'array')) return false;
    sup.changeLog = pushClearLog(sup, 'evidenceIds', sup.evidenceIds, []);
    sup.evidenceIds = [];
    return true;
  });

  // ── metrics/<id>.json ──────────────────────────────────────────────────────
  await processItemCollection(orgId, 'metrics', (m) => {
    let dirty = false;
    if (Array.isArray(m.readings)) {
      m.readings.forEach((r, idx) => {
        if (r && !isAlreadyEmpty(r.evidenceId, 'value')) {
          m.changeLog = pushClearLog(m, `readings[${idx}].evidenceId`, r.evidenceId, null);
          r.evidenceId = null;
          dirty = true;
        }
      });
    }
    return dirty;
  });

  // ── competence/program-<year>.json (every year) ────────────────────────────
  let competencePrograms = [];
  try { competencePrograms = await listOrgCollection(orgId, 'competence'); } catch { /* none */ }
  for (const prog of competencePrograms) {
    if (!prog || prog.year == null) continue;
    let dirty = false;
    if (Array.isArray(prog.roles)) {
      prog.roles.forEach((role, rIdx) => {
        if (role && Array.isArray(role.trainingActions)) {
          role.trainingActions.forEach((act, aIdx) => {
            if (act && !isAlreadyEmpty(act.evidenceId, 'value')) {
              prog.changeLog = pushClearLog(
                prog,
                `roles[${rIdx}].trainingActions[${aIdx}].evidenceId`,
                act.evidenceId,
                null
              );
              act.evidenceId = null;
              dirty = true;
            }
          });
        }
      });
    }
    if (dirty) {
      prog.updatedAt = new Date().toISOString();
      await writeOrgFile(orgId, `competence/program-${prog.year}.json`, prog);
    }
  }

  // ── awareness/program-<year>.json (every year) ─────────────────────────────
  let awarenessPrograms = [];
  try { awarenessPrograms = await listOrgCollection(orgId, 'awareness'); } catch { /* none */ }
  for (const prog of awarenessPrograms) {
    if (!prog || prog.year == null) continue;
    let dirty = false;
    if (Array.isArray(prog.activities)) {
      prog.activities.forEach((act, idx) => {
        if (act && !isAlreadyEmpty(act.evidenceId, 'value')) {
          prog.changeLog = pushClearLog(prog, `activities[${idx}].evidenceId`, act.evidenceId, null);
          act.evidenceId = null;
          dirty = true;
        }
      });
    }
    if (dirty) {
      prog.updatedAt = new Date().toISOString();
      await writeOrgFile(orgId, `awareness/program-${prog.year}.json`, prog);
    }
  }

  // ── nonconformities/<id>.json ──────────────────────────────────────────────
  await processItemCollection(orgId, 'nonconformities', (nc) => {
    let dirty = false;
    if (Array.isArray(nc.correctiveActions)) {
      nc.correctiveActions.forEach((ca, idx) => {
        if (ca && !isAlreadyEmpty(ca.evidenceId, 'value')) {
          nc.changeLog = pushClearLog(nc, `correctiveActions[${idx}].evidenceId`, ca.evidenceId, null);
          ca.evidenceId = null;
          dirty = true;
        }
      });
    }
    return dirty;
  });

  // ── profile.json — top-level org evidence + flag + date ───────────────────
  if (!isAlreadyEmpty(profile.topManagementCommitmentEvidenceId, 'value')) {
    profile.changeLog = pushClearLog(
      profile,
      'topManagementCommitmentEvidenceId',
      profile.topManagementCommitmentEvidenceId,
      null
    );
    profile.topManagementCommitmentEvidenceId = null;
  }
  profile.evidenceMigrationDone = true;
  profile.evidenceMigrationDate = todayIso();
  profile.updatedAt = new Date().toISOString();
  await writeOrgFile(orgId, 'profile.json', profile);
}
